import type { BenchMetric, BenchMetricKey, OverviewSurvey } from '@aithora/shared';
import { ord, pct100 } from './lib';

/**
 * Performance > Survey ready (design ConsoleV9 isSurveyTab, care-ops.js survey day), worded
 * from what the back end has: the CMS catalog's survey dates and last results
 * (GET /overview/survey), the building's CMS staffing figures (the benchmark), and the
 * pulled-away coverage (GET /facilities/:id/worklist/coverage). The design's open items,
 * risk grades, expected staffing, day-one readiness and survey-day tag readiness have no
 * feed yet: those say "Not connected yet", never a sample number.
 */

const RED = '#B3261E';
const OK = '#1F6B52';
const GREY = '#54606C';

/** "Nov 18, 2025" from YYYY-MM-DD (read as a calendar date, never shifted by time zone). */
export function dayLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

const monthName = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

/** "11 months ago", "1 month ago", "less than a month ago". */
export function monthsAgo(m: number | null | undefined): string | null {
  if (m == null || !Number.isFinite(m)) return null;
  const n = Math.round(m);
  if (n < 1) return 'less than a month ago';
  return n + (n === 1 ? ' month ago' : ' months ago');
}

export interface WhenCell { k: string; v: string; s: string }

/** When the surveyor may come: the last standard survey, the 15-month limit, the likely window. */
export function whenCells(s: OverviewSurvey | null, nowMs: number): WhenCell[] | null {
  if (!s || !s.available || !s.lastStandardSurvey) return null;
  const last = dayLabel(s.lastStandardSurvey)!;
  const out: WhenCell[] = [{ k: 'Last standard survey', v: last, s: monthsAgo(s.monthsSinceLast) ?? '' }];
  const due = s.dueBy ? new Date(s.dueBy + 'T12:00:00Z') : null;
  if (due && !Number.isNaN(due.getTime())) {
    out.push({ k: 'Must happen by', v: dayLabel(s.dueBy)!, s: '15-month federal limit' });
    if (due.getTime() < nowMs) out.push({ k: 'Likely window', v: 'Overdue', s: 'Past the 15-month limit' });
    else {
      // The statewide average may not pass 12 months (42 CFR 488.308): from about month 9 on,
      // the visit can come any day until the limit.
      const lastD = new Date(s.lastStandardSurvey + 'T12:00:00Z');
      const opens = new Date(Date.UTC(lastD.getUTCFullYear(), lastD.getUTCMonth() + 9, lastD.getUTCDate(), 12));
      const from = opens.getTime() <= nowMs ? 'Now' : monthName(opens);
      out.push({ k: 'Likely window', v: from + ' to ' + monthName(due), s: 'The state average is about 12 months' });
    }
  }
  return out;
}

export interface PastRow { d: string; kind: string; res: string }

/** The inspections the CMS catalog holds: the last standard survey (with its results) and the one before. */
export function pastRows(s: OverviewSurvey | null): PastRow[] {
  if (!s || !s.available) return [];
  const out: PastRow[] = [];
  const last = dayLabel(s.lastStandardSurvey);
  if (last) {
    const r = s.lastSurvey;
    const n = r?.deficiencies ?? null;
    const sev = r?.severeDeficiencies ?? null;
    const inf = r?.infectionDeficiencies ?? null;
    const parts: string[] = [];
    if (n != null) parts.push(n + (n === 1 ? ' citation' : ' citations') + (sev == null ? '' : sev === 0 ? ', none at harm level' : ', ' + sev + ' at harm level or above') + '.');
    if (inf != null && inf > 0) parts.push('Infection control (F880) cited.');
    out.push({ d: last, kind: 'Standard survey', res: parts.length ? parts.join(' ') : 'Results not in the CMS catalog.' });
  }
  const prior = dayLabel(s.priorStandardSurvey);
  if (prior) out.push({ d: prior, kind: 'Standard survey', res: 'The CMS catalog keeps citation counts for the latest survey only.' });
  return out;
}

/** "Last 2 inspections" (the design's "Last 3 inspections" counts what is on file). */
export const pastHead = (n: number) => (n === 1 ? 'Last inspection' : 'Last ' + n + ' inspections');

export interface StaffRow { k: string; v: string; tag: string; tagColor: string; note: string }

type Metrics = Partial<Record<BenchMetricKey, BenchMetric>>;

/** Staffing vs. how much care residents need: CMS's reported hours now; expected hours wait on a feed. */
export function staffRows(metrics: Metrics | null | undefined): StaffRow[] {
  const hrs = (key: 'totalHprd' | 'rnHprd', k: string): StaffRow => {
    const m = metrics?.[key];
    const p = pct100(m?.percentile);
    if (m?.value == null) return { k, v: 'Not on file', tag: '', tagColor: GREY, note: 'CMS has not published this figure for the building.' };
    return {
      k,
      v: m.value.toFixed(2) + ' reported',
      tag: p == null ? '' : ord(p) + ' percentile',
      tagColor: p == null ? GREY : p < 40 ? RED : p >= 50 ? OK : GREY,
      note: 'Expected hours for your residents’ care needs are not connected yet.',
    };
  };
  return [
    hrs('totalHprd', 'Nurse hours per resident per day'),
    hrs('rnHprd', 'RN hours per resident per day'),
    { k: 'Weekend vs weekday nurse hours', v: 'Not connected yet', tag: '', tagColor: GREY, note: 'Care Compare shows weekend staffing; surveyors look at it.' },
    { k: 'RN on site 8 hours every day', v: 'Not connected yet', tag: '', tagColor: GREY, note: 'Federal rule: an RN 8 hours a day, 7 days a week.' },
    { k: 'Facility assessment', v: 'Not connected yet', tag: '', tagColor: GREY, note: 'Must be reviewed at least once a year and match the staffing your residents need.' },
  ];
}

export interface RbsRow { t: string; state: 'met' | 'not' | 'unknown' }

const RBS = [
  '5-star overall rating',
  'Staffing rating 3 ★ or better',
  'No harm, immediate jeopardy or substandard care citation last cycle',
  'Standard survey within 18 months',
  'Health inspection score in the better half of the state',
  'No staffing waiver in effect',
  'No failed staffing data (PBJ) audit',
  'No failed MDS audit',
  'Fewer than 2 residents 65+ newly coded with schizophrenia',
  'No change of owner since the last standard survey',
  'Not a Special Focus Facility candidate',
] as const;

/**
 * The shorter (Risk-Based) survey's eleven conditions. Four are checked from CMS figures on
 * file; the rest wait on a feed and are said so, never counted as met.
 */
export function rbsRows(metrics: Metrics | null | undefined, s: OverviewSurvey | null): { head: string; rows: RbsRow[] } {
  const star = metrics?.overallRating?.value ?? null;
  const staff = metrics?.staffingRating?.value ?? null;
  const months = s?.available ? s.monthsSinceLast ?? null : null;
  const hiP = pct100(metrics?.healthSurveyScore?.percentile ?? null);
  const known: Partial<Record<(typeof RBS)[number], boolean | null>> = {
    '5-star overall rating': star == null ? null : star >= 5,
    'Staffing rating 3 ★ or better': staff == null ? null : staff >= 3,
    'Standard survey within 18 months': months == null ? null : months <= 18,
    'Health inspection score in the better half of the state': hiP == null ? null : hiP >= 50,
  };
  const rows: RbsRow[] = RBS.map((t) => {
    const k = known[t];
    if (k === true) return { t: '✓ ' + t, state: 'met' };
    if (k === false) return { t: '✗ ' + t + (t === '5-star overall rating' && star != null ? '. Rated ' + star + ' ★' : ''), state: 'not' };
    return { t: '? ' + t + ': not known yet', state: 'unknown' };
  });
  const met = rows.filter((r) => r.state === 'met').length;
  const unknown = rows.filter((r) => r.state === 'unknown').length;
  return { head: 'Shorter survey: ' + met + ' of 11 met' + (unknown ? ' · ' + unknown + ' not known yet' : ''), rows };
}

export const rbsColor = (st: RbsRow['state']) => (st === 'met' ? OK : st === 'not' ? RED : GREY);

/** The five survey-day tags (care-ops.js TAGS) and the page their records open on. */
export const SURVEY_DAY_TAGS: ReadonlyArray<{ tag: string; name: string; path: string }> = [
  { tag: 'F689', name: 'Falls and accidents', path: '/residents' },
  { tag: 'F880', name: 'Infection control', path: '/residents' },
  { tag: 'F684', name: 'Quality of care', path: '/worklist' },
  { tag: 'F812', name: 'Food safety', path: '/settings' },
  { tag: 'F600', name: 'Abuse and neglect', path: '/guardian' },
];

/** The entrance-conference list (design srvDocs): what Aithora can print once connected, and what it never holds. */
export const DAY_ONE_DOCS: ReadonlyArray<{ name: string; note: string; fromAithora: boolean }> = [
  { name: 'Census and resident list with rooms', note: 'Not connected yet', fromAithora: true },
  { name: 'Residents with pressure ulcers', note: 'Not connected yet', fromAithora: true },
  { name: 'Residents on antipsychotic or other mental-health medicines', note: 'Not connected yet', fromAithora: true },
  { name: 'Falls in the last 30 days', note: 'Not connected yet', fromAithora: true },
  { name: 'Infection log and antibiotic use, last 3 months', note: 'Not connected yet', fromAithora: true },
  { name: 'Residents on hospice, dialysis or oxygen', note: 'Not connected yet', fromAithora: true },
  { name: 'Nurse staffing schedule for the survey dates', note: 'Not in Aithora yet. Print it from your scheduling system.', fromAithora: false },
  { name: 'Facility assessment', note: 'Not connected yet', fromAithora: true },
  { name: 'Quality improvement (QAPI) plan and last 3 meeting notes', note: 'Not in Aithora. Keep a printed copy at the front desk.', fromAithora: false },
  { name: 'Admission packet, including any arbitration agreement', note: 'Not in Aithora. Keep a printed copy at the front desk.', fromAithora: false },
];

/** The page's top line (design srvLead / srvSub), with no open-item count until one is connected. */
export function surveyLead(fa: boolean): { lead: string; sub: string } {
  return {
    lead: 'The next standard survey can come any day now.',
    sub: fa
      ? 'The areas surveyors cite most show here, with what to fix first, once this building’s records are connected to Aithora.'
      : 'The areas surveyors cite most show here once the building’s records are connected to Aithora. The facility team fixes them.',
  };
}

/** The secure surveyor link's line once made (care-ops.js sdLink), for the hours asked. */
export const linkReady = (hours: number) => 'Secure surveyor link ready · read-only · expires in ' + hours + (hours === 1 ? ' hour' : ' hours') + ' · every view is logged';
